import { useEffect, useState } from 'react';
import axios from 'axios';
import { Wallet, Trophy } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PaidThisMonthCard — Round 84k (D).
 *
 * Calendar-month-to-date dividend summary. Shows total received,
 * payment count, biggest single payer, and the top 5 contributors so
 * the user gets that "this is what my paycheck looked like" moment.
 *
 * Powered by GET /api/portfolio/paid-this-month.
 */
export default function PaidThisMonthCard({ refreshKey = 0, onStockClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/paid-this-month`, { withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading || !data) return null;
  const total = data.total_received || 0;
  const count = data.payment_count || 0;
  if (count === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-border bg-card/40 p-4 mb-4 text-center text-xs text-muted-foreground"
        data-testid="paid-this-month-empty"
      >
        <Wallet className="w-4 h-4 inline mr-1.5 opacity-70" />
        No dividend payments received yet this month ({data.month_label}). Sit back — your next payday is coming.
      </div>
    );
  }
  const biggest = data.biggest_payer;
  const topPayers = data.top_payers || [];

  return (
    <div
      className="rounded-xl border border-emerald-300/40 dark:border-emerald-700/40 bg-gradient-to-br from-emerald-50/60 to-emerald-100/30 dark:from-emerald-950/30 dark:to-emerald-900/10 p-4 mb-4"
      data-testid="paid-this-month-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-sm font-bold">What Paid Me This Month</h3>
          <span className="text-[11px] text-muted-foreground">· {data.month_label}</span>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-300" data-testid="paid-this-month-total">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
            from {count} payment{count === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {biggest && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-700/50">
          <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs text-muted-foreground">Biggest contributor: </span>
            <button
              type="button"
              onClick={() => onStockClick?.(biggest.ticker)}
              className="font-bold text-foreground hover:text-primary transition-colors"
              data-testid="paid-biggest-payer"
            >
              {biggest.ticker}
            </button>
            <span className="font-mono font-bold ml-1 text-amber-700 dark:text-amber-300">
              ${biggest.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {topPayers.length > 1 && (
        <div className="space-y-1" data-testid="paid-top-payers">
          {topPayers.map((p, i) => (
            <button
              key={p.ticker}
              type="button"
              onClick={() => onStockClick?.(p.ticker)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-muted/60 transition-colors text-left"
              data-testid={`paid-payer-${p.ticker}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] text-muted-foreground w-4">{i + 1}.</span>
                <span className="font-bold text-sm">{p.ticker}</span>
              </div>
              <span className="font-mono font-semibold text-sm">
                ${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
